/**
 * Cookies Utility Library (Refactored - Production Hardened)
 */

import Cookies from "js-cookie";
import { VERIFY_AGE } from "../../constants/common";
import { reportError, reportWarning } from "../../sentry";

/* -------------------------------------------------------
   ENV / DOMAIN CONFIG
-------------------------------------------------------- */

const JUICYHOUSE_PARENT_COOKIE_DOMAIN = ".juicyhouse.com";

const isBrowser = () => typeof window !== "undefined";

const isHttps = () =>
  isBrowser() && window.location.protocol === "https:";

const isLocalDevHost = (hostname) =>
  hostname === "localhost" ||
  hostname.endsWith(".localhost") ||
  hostname === "127.0.0.1" ||
  hostname === "[::1]" ||
  /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname) ||
  !hostname.includes(".");

/**
 * Resolve cookie domain (stable single source of truth)
 */
const getCookieDomain = () => {
  if (!isBrowser()) return undefined;

  const hostname = window.location.hostname;

  if (isLocalDevHost(hostname)) return undefined;

  if (hostname === "juicyhouse.com" || hostname.endsWith(".juicyhouse.com")) {
    return JUICYHOUSE_PARENT_COOKIE_DOMAIN;
  }

  return undefined;
};

const getBaseCookieConfig = () => {
  const domain = getCookieDomain();
  return {
    expires: 365,
    path: "/",
    sameSite: "lax",
    secure: false,
    ...(domain ? { domain } : {}),
  };
};

/* -------------------------------------------------------
   LOCAL STORAGE HELPERS
-------------------------------------------------------- */

const safeLocalGet = (key) => {
  if (!isBrowser()) return undefined;
  try {
    return window.localStorage.getItem(key) || undefined;
  } catch {
    return undefined;
  }
};

const safeLocalSet = (key, value) => {
  if (!isBrowser()) return false;
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const safeLocalRemove = (key) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {}
};

/* -------------------------------------------------------
   COOKIE CORE
-------------------------------------------------------- */

const readCookieValue = (name) => Cookies.get(name);

export const setCookie = (name, value, options = {}) => {
  try {
    const config = {
      ...getBaseCookieConfig(),
      secure: isHttps(),
      ...options,
    };

    Cookies.set(name, value, config);

    const verify = readCookieValue(name);

    // Only safely verify primitives
    const isPrimitive =
      typeof value === "string" || typeof value === "number" || typeof value === "boolean";

    if (isPrimitive && verify !== String(value)) {
      reportWarning(null, "Cookie verification mismatch", {
        extra: { name, expected: value, got: verify },
      });
      return false;
    }

    return true;
  } catch (err) {
    reportError(err, "setCookie", { extra: { name } });
    return false;
  }
};

export const getCookie = (name) => Cookies.get(name);

export const removeCookie = (name, extraOptions = {}) => {
  const domain = getCookieDomain();

  const baseOptions = {
    path: "/",
    ...(domain ? { domain } : {}),
    ...extraOptions,
  };

  // Try all likely permutations (Safari/ITP robustness)
  const variants = [
    baseOptions,
    { ...baseOptions, secure: true },
    { ...baseOptions, secure: false },
  ];

  variants.forEach((opts) => Cookies.remove(name, opts));

  return getCookie(name) === undefined;
};

export const hasCookie = (name) => getCookie(name) !== undefined;

export const getAllCookies = () => Cookies.get();

/* -------------------------------------------------------
   COOKIE + LOCAL STORAGE FALLBACK
-------------------------------------------------------- */

const setWithFallback = (cookieKey, value, localKey) => {
  const cookieOk = setCookie(cookieKey, value);
  const localOk = safeLocalSet(localKey, value);
  return cookieOk || localOk;
};

const getWithFallback = (cookieKey, localKey) =>
  getCookie(cookieKey) ?? safeLocalGet(localKey);

/* -------------------------------------------------------
   COOKIE KEYS
-------------------------------------------------------- */

export const COOKIE_KEYS = {
  CONSENT: "cookiesConsent",
  USER_PREFERENCES: "userPreferences",
  THEME: "theme",
  LANGUAGE: "language",
};

/* -------------------------------------------------------
   CONSENT MODEL
-------------------------------------------------------- */

const CONSENT_LOCAL_KEY = "jh_cookiesConsent";

export const CONSENT_TYPES = {
  ALL: "all",
  NECESSARY: "necessary",
  ANALYTICS: "analytics",
  MARKETING: "marketing",
  NONE: "none",
};

export const setCookieConsent = (tier) =>
  setWithFallback(COOKIE_KEYS.CONSENT, tier, CONSENT_LOCAL_KEY);

export const getCookieConsent = () =>
  getWithFallback(COOKIE_KEYS.CONSENT, CONSENT_LOCAL_KEY);

/**
 * Correct + deterministic consent logic
 */
export const hasConsent = (type) => {
  const current = getCookieConsent();
  if (!current) return false;

  if (type === CONSENT_TYPES.NECESSARY) return true;
  if (current === CONSENT_TYPES.ALL) return true;

  return current === type;
};

export const hasAnyConsent = () => Boolean(getCookieConsent());

export const revokeConsent = () => {
  removeCookie(COOKIE_KEYS.CONSENT);
  safeLocalRemove(CONSENT_LOCAL_KEY);
};

/* -------------------------------------------------------
   USER PREFERENCES (NO MUTATION, SAFE JSON)
-------------------------------------------------------- */

export const setUserPreference = (key, value) => {
  const prefs = getUserPreferences();

  const updated = {
    ...prefs,
    [key]: value,
  };

  return setCookie(
    COOKIE_KEYS.USER_PREFERENCES,
    JSON.stringify(updated)
  );
};

export const getUserPreferences = () => {
  try {
    const raw = getCookie(COOKIE_KEYS.USER_PREFERENCES);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch (err) {
    reportWarning(err, "parse user preferences");
    return {};
  }
};

export const getUserPreference = (key, fallback = null) => {
  const prefs = getUserPreferences();
  return prefs[key] ?? fallback;
};

/* -------------------------------------------------------
   SIMPLE HELPERS
-------------------------------------------------------- */

export const setTheme = (theme) =>
  setCookie(COOKIE_KEYS.THEME, theme);

export const getTheme = () =>
  getCookie(COOKIE_KEYS.THEME);

export const setLanguage = (lang) =>
  setCookie(COOKIE_KEYS.LANGUAGE, lang);

export const getLanguage = () =>
  getCookie(COOKIE_KEYS.LANGUAGE);

/* -------------------------------------------------------
   COOKIE BANNER LOGIC
-------------------------------------------------------- */

export const shouldShowCookieBanner = () => {
  const consent = hasAnyConsent();
  const ageVerified = getCookie(VERIFY_AGE);

  // If explicitly blocked → respect it
  const ageBlocked = ageVerified === "false";

  if (ageBlocked) return false;

  return !consent;
};

export const handleConsentAcceptance = (tier) => {
  const ok = setCookieConsent(tier);

  if (ok && isBrowser()) {
    window.dispatchEvent(
      new CustomEvent("cookieConsentChanged", {
        detail: { tier },
      })
    );
  }

  return ok;
};

/* -------------------------------------------------------
   EXPORT RAW LIBRARY
-------------------------------------------------------- */

export { Cookies };